import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/cn'
import { categoriesOf } from '@/lib/useStorefrontCategories'
import ProductCard from '@/components/storefront/ProductCard'
import CategorySidebar from '@/components/storefront/CategorySidebar'
import MemberPricingPromo from '@/components/storefront/MemberPricingPromo'
import Reveal from '@/components/storefront/Reveal'
import SectionHeading from '@/components/storefront/SectionHeading'
import Skeleton from '@/components/ui/Skeleton'
import type { StorefrontProduct, Tier } from '@/types'

/**
 * Storefront browsing: a category rail beside the product grid, with the rail
 * collapsing to a scrollable pill row below `lg` and the member-pricing promo
 * moving under the grid at the same breakpoint.
 *
 * Selection is controlled by the parent, so the home page can filter in local
 * state while the products page drives it from `?category=`.
 */
export default function CategoryBrowser({
  products,
  tiers,
  isLoading,
  value,
  onChange,
  title,
  subtitle,
  limit,
  showViewAll = false,
}: {
  products: StorefrontProduct[] | undefined
  tiers: Tier[] | undefined
  isLoading: boolean
  value: string | null
  onChange: (next: string | null) => void
  title: string
  subtitle?: string
  limit?: number
  showViewAll?: boolean
}) {
  const all = products ?? []
  const categories = categoriesOf(products)
  const visible = value ? all.filter((p) => p.category === value) : all
  const shown = limit ? visible.slice(0, limit) : visible

  const pills: { key: string; label: string; slug: string | null }[] = [
    { key: '__all', label: 'All', slug: null },
    ...categories.map((c) => ({ key: c.name, label: c.name, slug: c.name })),
  ]

  return (
    <section className="mx-auto w-full max-w-6xl px-5 py-12">
      {/* Only the header animates here - the grid holds a sticky rail, and the
          section sits right under the full-viewport hero anyway. */}
      <Reveal>
        <div className="flex items-end justify-between gap-4">
          <SectionHeading title={title} subtitle={subtitle} />
          <p className="shrink-0 text-sm text-foreground/50">
            {visible.length} {visible.length === 1 ? 'product' : 'products'}
          </p>
        </div>
      </Reveal>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr] lg:gap-8">
        {/* Rail. The sticky class lives on this wrapper, never on a .box - that
            recipe sets position: relative late in the cascade and would kill it. */}
        <div className="hidden lg:block">
          <div className="lg:sticky lg:top-[86px] lg:space-y-4">
            <CategorySidebar categories={categories} total={all.length} value={value} onChange={onChange} />
            {tiers && tiers.length > 0 && <MemberPricingPromo tiers={tiers} />}
          </div>
        </div>

        <div>
          {/* The rail's small-screen form. */}
          <div className="scrollbar-thin -mx-1 mb-5 flex gap-2 overflow-x-auto px-1 pb-1 lg:hidden">
            {pills.map((pill) => (
              <button
                key={pill.key}
                type="button"
                onClick={() => onChange(pill.slug)}
                className={cn(
                  'shrink-0 rounded-lg border px-3.5 py-1.5 text-sm font-medium transition-colors',
                  value === pill.slug
                    ? 'border-foreground/25 bg-foreground/5 font-semibold text-foreground'
                    : 'border-foreground/20 text-foreground/70 hover:bg-foreground/5',
                )}
              >
                {pill.label}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-64 rounded-2xl sm:h-96" />
              ))}
            </div>
          ) : shown.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-foreground/20 p-12 text-center text-sm text-foreground/60">
              No products in this category yet.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
              {shown.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}

              {/* Closing tile, so a thin category still fills more than one cell. */}
              {showViewAll && (
                <Link
                  to={value ? `/products?category=${encodeURIComponent(value)}` : '/products'}
                  className="box flex flex-col items-center justify-center gap-2 p-4 text-center text-foreground/60 transition-colors hover:text-primary"
                >
                  <span className="flex size-10 items-center justify-center rounded-full border border-foreground/15 bg-background shadow-sm">
                    <ArrowRight className="size-4" />
                  </span>
                  <span className="text-sm font-medium">{value ? `View all ${value}` : 'View all products'}</span>
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
